import { c as _c } from "react/compiler-runtime";
import { composeEventHandlers } from "@radix-ui/primitive";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { Primitive } from "@radix-ui/react-primitive";
import { Item as RovingFocusGroupItem, Root as RovingFocusGroupRoot } from "@radix-ui/react-roving-focus";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { Command as CommandPrimitive } from "cmdk";
import { Popover as PopoverPrimitive } from "radix-ui";
import { Children, createContext, Fragment, isValidElement, useCallback, useContext, useMemo, useRef, useState } from "react";
export type ComboboxContextProps = {
  inputValue: string;
  onInputValueChange: (inputValue: string, reason: "inputChange" | "itemSelect" | "clearClick") => void;
  onInputBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTabStopId: string | null;
  onCurrentTabStopIdChange: (currentTabStopId: string | null) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  tagGroupRef: React.RefObject<React.ComponentRef<typeof RovingFocusGroupRoot> | null>;
  disabled?: boolean;
  required?: boolean;
} & (Required<Pick<ComboboxSingleProps, "type" | "value" | "onValueChange">> | Required<Pick<ComboboxMultipleProps, "type" | "value" | "onValueChange">>);
const ComboboxContext = createContext<ComboboxContextProps>({
  type: "single",
  value: "",
  onValueChange: () => {
    /* empty */
  },
  inputValue: "",
  onInputValueChange: () => {
    /* empty */
  },
  onInputBlur: () => {
    /* empty */
  },
  open: false,
  onOpenChange: () => {
    /* empty */
  },
  currentTabStopId: null,
  onCurrentTabStopIdChange: () => {
    /* empty */
  },
  inputRef: {
    current: null
  },
  tagGroupRef: {
    current: null
  },
  disabled: false,
  required: false
});
function useCombobox() {
  return useContext(ComboboxContext);
}
export type ComboboxType = "single" | "multiple";
export interface ComboboxBaseProps extends React.ComponentProps<typeof PopoverPrimitive.Root>, Omit<React.ComponentProps<typeof CommandPrimitive>, "value" | "defaultValue" | "onValueChange"> {
  type?: ComboboxType | undefined;
  inputValue?: string;
  defaultInputValue?: string;
  onInputValueChange?: (inputValue: string, reason: "inputChange" | "itemSelect" | "clearClick") => void;
  onInputBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
}
export type ComboboxValue<T extends ComboboxType = "single"> = T extends "single" ? string : T extends "multiple" ? string[] : never;
export interface ComboboxSingleProps {
  type: "single";
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}
export interface ComboboxMultipleProps {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
}
export type ComboboxProps = ComboboxBaseProps & (ComboboxSingleProps | ComboboxMultipleProps);
function Combobox(t0) {
  const $ = _c(60);
  let children;
  let defaultInputValue;
  let defaultOpen;
  let defaultValue;
  let disabled;
  let inputValueProp;
  let modal;
  let onInputBlur;
  let onInputValueChange;
  let onOpenChange;
  let onValueChange;
  let openProp;
  let props;
  let required;
  let type;
  let valueProp;
  if ($[0] !== t0) {
    ({
      type,
      open: openProp,
      onOpenChange,
      defaultOpen,
      modal,
      children,
      value: valueProp,
      defaultValue,
      onValueChange,
      inputValue: inputValueProp,
      defaultInputValue,
      onInputValueChange,
      onInputBlur,
      disabled,
      required,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = defaultInputValue;
    $[3] = defaultOpen;
    $[4] = defaultValue;
    $[5] = disabled;
    $[6] = inputValueProp;
    $[7] = modal;
    $[8] = onInputBlur;
    $[9] = onInputValueChange;
    $[10] = onOpenChange;
    $[11] = onValueChange;
    $[12] = openProp;
    $[13] = props;
    $[14] = required;
    $[15] = type;
    $[16] = valueProp;
  } else {
    children = $[1];
    defaultInputValue = $[2];
    defaultOpen = $[3];
    defaultValue = $[4];
    disabled = $[5];
    inputValueProp = $[6];
    modal = $[7];
    onInputBlur = $[8];
    onInputValueChange = $[9];
    onOpenChange = $[10];
    onValueChange = $[11];
    openProp = $[12];
    props = $[13];
    required = $[14];
    type = $[15];
    valueProp = $[16];
  }
  let t1;
  if ($[17] !== defaultValue || $[18] !== type) {
    t1 = defaultValue ?? type === "multiple" ? [] : "";
    $[17] = defaultValue;
    $[18] = type;
    $[19] = t1;
  } else {
    t1 = $[19];
  }
  const t2 = t1 as ComboboxValue<ComboboxType>;
  const t3 = onValueChange as (value: ComboboxValue<ComboboxType>) => void;
  let t4;
  if ($[20] !== t2 || $[21] !== t3 || $[22] !== valueProp) {
    t4 = {
      prop: valueProp,
      defaultProp: t2,
      onChange: t3
    };
    $[20] = t2;
    $[21] = t3;
    $[22] = valueProp;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const [value, setValue] = useControllableState(t4);
  const t5 = defaultInputValue ?? "";
  let t6;
  if ($[24] !== inputValueProp || $[25] !== t5) {
    t6 = {
      prop: inputValueProp,
      defaultProp: t5
    };
    $[24] = inputValueProp;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const [inputValue, setInputValue] = useControllableState(t6);
  const t7 = defaultOpen ?? false;
  let t8;
  if ($[27] !== onOpenChange || $[28] !== openProp || $[29] !== t7) {
    t8 = {
      prop: openProp,
      defaultProp: t7,
      onChange: onOpenChange
    };
    $[27] = onOpenChange;
    $[28] = openProp;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const [open, setOpen] = useControllableState(t8);
  const [currentTabStopId, setCurrentTabStopId] = useState(null);
  const inputRef = useRef(null);
  const tagGroupRef = useRef(null);
  let t9;
  if ($[31] !== onInputValueChange || $[32] !== setInputValue) {
    t9 = (inputValue_0, reason) => {
      setInputValue(inputValue_0);
      onInputValueChange?.(inputValue_0, reason);
    };
    $[31] = onInputValueChange;
    $[32] = setInputValue;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleInputValueChange = t9;
  let t10;
  if ($[34] !== currentTabStopId || $[35] !== disabled || $[36] !== handleInputValueChange || $[37] !== inputValue || $[38] !== onInputBlur || $[39] !== open || $[40] !== required || $[41] !== setOpen || $[42] !== setValue || $[43] !== type || $[44] !== value) {
    t10 = {
      type,
      value,
      onValueChange: setValue,
      inputValue,
      onInputValueChange: handleInputValueChange,
      onInputBlur,
      open,
      onOpenChange: setOpen,
      currentTabStopId,
      onCurrentTabStopIdChange: setCurrentTabStopId,
      inputRef,
      tagGroupRef,
      disabled,
      required
    };
    $[34] = currentTabStopId;
    $[35] = disabled;
    $[36] = handleInputValueChange;
    $[37] = inputValue;
    $[38] = onInputBlur;
    $[39] = open;
    $[40] = required;
    $[41] = setOpen;
    $[42] = setValue;
    $[43] = type;
    $[44] = value;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  const t11 = t10 as ComboboxContextProps;
  let t12;
  if ($[46] !== open) {
    t12 = !open && <CommandPrimitive.List aria-hidden={true} hidden={true} />;
    $[46] = open;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== children || $[49] !== props || $[50] !== t12) {
    t13 = <CommandPrimitive data-slot="combobox" {...props}>{children}{t12}</CommandPrimitive>;
    $[48] = children;
    $[49] = props;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== modal || $[53] !== open || $[54] !== setOpen || $[55] !== t13) {
    t14 = <PopoverPrimitive.Root open={open} onOpenChange={setOpen} modal={modal}>{t13}</PopoverPrimitive.Root>;
    $[52] = modal;
    $[53] = open;
    $[54] = setOpen;
    $[55] = t13;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== t11 || $[58] !== t14) {
    t15 = <ComboboxContext.Provider value={t11}>{t14}</ComboboxContext.Provider>;
    $[57] = t11;
    $[58] = t14;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
}
function ComboboxTagGroup(t0) {
  const $ = _c(11);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  const {
    currentTabStopId,
    onCurrentTabStopIdChange,
    tagGroupRef,
    type
  } = useCombobox();
  if (type !== "multiple") {
    throw new Error("<ComboboxTagGroup> should only be used when type is \"multiple\"");
  }
  const composedRefs = useComposedRefs(ref, tagGroupRef);
  let t1;
  if ($[3] !== onCurrentTabStopIdChange) {
    t1 = () => {
      onCurrentTabStopIdChange(null);
    };
    $[3] = onCurrentTabStopIdChange;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== composedRefs || $[6] !== currentTabStopId || $[7] !== onCurrentTabStopIdChange || $[8] !== props || $[9] !== t1) {
    t2 = <RovingFocusGroupRoot data-slot="combobox-tag-group" ref={composedRefs} tabIndex={-1} currentTabStopId={currentTabStopId} onCurrentTabStopIdChange={onCurrentTabStopIdChange} onBlur={t1} {...props} />;
    $[5] = composedRefs;
    $[6] = currentTabStopId;
    $[7] = onCurrentTabStopIdChange;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
export interface ComboboxTagGroupItemProps extends Omit<React.ComponentProps<typeof RovingFocusGroupItem>, "children"> {
  children?: React.ReactNode;
  value: string;
  disabled?: boolean;
}
const ComboboxTagGroupItemContext = createContext<Pick<ComboboxTagGroupItemProps, "value" | "disabled">>({
  value: "",
  disabled: false
});
function useComboboxTagGroupItem() {
  return useContext(ComboboxTagGroupItemContext);
}
function ComboboxTagGroupItem(t0) {
  const $ = _c(33);
  let disabled;
  let onClick;
  let onKeyDown;
  let props;
  let valueProp;
  if ($[0] !== t0) {
    ({
      onClick,
      onKeyDown,
      value: valueProp,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = disabled;
    $[2] = onClick;
    $[3] = onKeyDown;
    $[4] = props;
    $[5] = valueProp;
  } else {
    disabled = $[1];
    onClick = $[2];
    onKeyDown = $[3];
    props = $[4];
    valueProp = $[5];
  }
  const {
    value,
    onValueChange,
    inputRef,
    currentTabStopId,
    type
  } = useCombobox();
  if (type !== "multiple") {
    throw new Error("<ComboboxTagGroupItem> should only be used when type is \"multiple\"");
  }
  let t1;
  if ($[6] !== value) {
    t1 = value.at(-1);
    $[6] = value;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const lastItemValue = t1;
  let t2;
  if ($[8] !== disabled || $[9] !== valueProp) {
    t2 = {
      value: valueProp,
      disabled
    };
    $[8] = disabled;
    $[9] = valueProp;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== currentTabStopId || $[12] !== inputRef || $[13] !== lastItemValue || $[14] !== onKeyDown || $[15] !== onValueChange || $[16] !== value) {
    t3 = event => {
      onKeyDown?.(event);
      if (event.key === "Escape") {
        inputRef.current?.focus();
      }
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        inputRef.current?.focus();
      }
      if (event.key === "ArrowRight" && currentTabStopId === lastItemValue) {
        inputRef.current?.focus();
      }
      if (event.key === "Backspace" || event.key === "Delete") {
        onValueChange(value.filter(v => v !== currentTabStopId));
        inputRef.current?.focus();
      }
    };
    $[11] = currentTabStopId;
    $[12] = inputRef;
    $[13] = lastItemValue;
    $[14] = onKeyDown;
    $[15] = onValueChange;
    $[16] = value;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== disabled || $[19] !== inputRef || $[20] !== onClick) {
    t4 = event_0 => {
      onClick?.(event_0);
      if (disabled) {
        inputRef.current?.focus();
      }
    };
    $[18] = disabled;
    $[19] = inputRef;
    $[20] = onClick;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  const t5 = !disabled;
  const t6 = valueProp === lastItemValue;
  let t7;
  if ($[22] !== disabled || $[23] !== props || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== valueProp) {
    t7 = <RovingFocusGroupItem data-slot="combobox-tag-group-item" onKeyDown={t3} onClick={t4} tabStopId={valueProp} focusable={t5} data-disabled={disabled} active={t6} {...props} />;
    $[22] = disabled;
    $[23] = props;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = valueProp;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== t2 || $[31] !== t7) {
    t8 = <ComboboxTagGroupItemContext.Provider value={t2}>{t7}</ComboboxTagGroupItemContext.Provider>;
    $[30] = t2;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  return t8;
}
function ComboboxTagGroupItemRemove(t0) {
  const $ = _c(14);
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onClick;
    $[2] = props;
  } else {
    onClick = $[1];
    props = $[2];
  }
  const {
    value,
    onValueChange,
    type
  } = useCombobox();
  if (type !== "multiple") {
    throw new Error("<ComboboxTagGroupItemRemove> should only be used when type is \"multiple\"");
  }
  const {
    value: valueProp,
    disabled
  } = useComboboxTagGroupItem();
  let t1;
  if ($[3] !== onValueChange || $[4] !== value || $[5] !== valueProp) {
    t1 = () => {
      onValueChange(value.filter(v => v !== valueProp));
    };
    $[3] = onValueChange;
    $[4] = value;
    $[5] = valueProp;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== onClick || $[8] !== t1) {
    t2 = composeEventHandlers(onClick, t1);
    $[7] = onClick;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== disabled || $[11] !== props || $[12] !== t2) {
    t3 = <Primitive.button data-slot="combobox-tag-group-item-remove" aria-hidden={true} tabIndex={-1} disabled={disabled} onClick={t2} {...props} />;
    $[10] = disabled;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function ComboboxInput(t0) {
  const $ = _c(50);
  let onBlur;
  let onFocus;
  let onKeyDown;
  let onMouseDown;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      onKeyDown,
      onMouseDown,
      onFocus,
      onBlur,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onBlur;
    $[2] = onFocus;
    $[3] = onKeyDown;
    $[4] = onMouseDown;
    $[5] = props;
    $[6] = ref;
  } else {
    onBlur = $[1];
    onFocus = $[2];
    onKeyDown = $[3];
    onMouseDown = $[4];
    props = $[5];
    ref = $[6];
  }
  const {
    type,
    inputValue,
    onInputValueChange,
    onInputBlur,
    open,
    onOpenChange,
    value,
    onValueChange,
    inputRef,
    disabled,
    required,
    tagGroupRef
  } = useCombobox();
  const composedRefs = useComposedRefs(ref, inputRef);
  let t1;
  if ($[7] !== onInputValueChange || $[8] !== onOpenChange || $[9] !== onValueChange || $[10] !== open || $[11] !== type) {
    t1 = search => {
      if (!open) {
        onOpenChange(true);
      }
      setTimeout(() => {
        onInputValueChange(search, "inputChange");
      });
      if (!search && type === "single") {
        onValueChange("");
      }
    };
    $[7] = onInputValueChange;
    $[8] = onOpenChange;
    $[9] = onValueChange;
    $[10] = open;
    $[11] = type;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== inputValue || $[14] !== onKeyDown || $[15] !== onOpenChange || $[16] !== onValueChange || $[17] !== open || $[18] !== tagGroupRef || $[19] !== type || $[20] !== value) {
    t2 = event => {
      onKeyDown?.(event);
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        if (!open) {
          event.preventDefault();
          onOpenChange(true);
        }
      }
      if (type !== "multiple") {
        return;
      }
      if (event.key === "ArrowLeft" && !inputValue && value.length) {
        tagGroupRef.current?.focus();
      }
      if (event.key === "Backspace" && !inputValue) {
        onValueChange(value.slice(0, -1));
      }
    };
    $[13] = inputValue;
    $[14] = onKeyDown;
    $[15] = onOpenChange;
    $[16] = onValueChange;
    $[17] = open;
    $[18] = tagGroupRef;
    $[19] = type;
    $[20] = value;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  let t3;
  if ($[22] !== inputValue || $[23] !== onOpenChange || $[24] !== open) {
    t3 = () => {
      onOpenChange(!!inputValue || !open);
    };
    $[22] = inputValue;
    $[23] = onOpenChange;
    $[24] = open;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== onMouseDown || $[27] !== t3) {
    t4 = composeEventHandlers(onMouseDown, t3);
    $[26] = onMouseDown;
    $[27] = t3;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== onOpenChange) {
    t5 = () => {
      onOpenChange(true);
    };
    $[29] = onOpenChange;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== onFocus || $[32] !== t5) {
    t6 = composeEventHandlers(onFocus, t5);
    $[31] = onFocus;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== onInputBlur) {
    t7 = event_0 => {
      if (!event_0.relatedTarget?.hasAttribute("cmdk-list")) {
        onInputBlur?.(event_0);
      }
    };
    $[34] = onInputBlur;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] !== onBlur || $[37] !== t7) {
    t8 = composeEventHandlers(onBlur, t7);
    $[36] = onBlur;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== composedRefs || $[40] !== disabled || $[41] !== inputValue || $[42] !== props || $[43] !== required || $[44] !== t1 || $[45] !== t2 || $[46] !== t4 || $[47] !== t6 || $[48] !== t8) {
    t9 = <CommandPrimitive.Input data-slot="combobox-input" ref={composedRefs} disabled={disabled} required={required} value={inputValue} onValueChange={t1} onKeyDown={t2} onMouseDown={t4} onFocus={t6} onBlur={t8} {...props} />;
    $[39] = composedRefs;
    $[40] = disabled;
    $[41] = inputValue;
    $[42] = props;
    $[43] = required;
    $[44] = t1;
    $[45] = t2;
    $[46] = t4;
    $[47] = t6;
    $[48] = t8;
    $[49] = t9;
  } else {
    t9 = $[49];
  }
  return t9;
}
function ComboboxClear(t0) {
  const $ = _c(14);
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onClick;
    $[2] = props;
  } else {
    onClick = $[1];
    props = $[2];
  }
  const {
    value,
    onValueChange,
    inputValue,
    onInputValueChange,
    type
  } = useCombobox();
  const isValueEmpty = type === "single" ? !value : !value.length;
  const t1 = isValueEmpty && !inputValue;
  let t2;
  if ($[3] !== onInputValueChange || $[4] !== onValueChange || $[5] !== type) {
    t2 = () => {
      if (type === "single") {
        onValueChange("");
      } else {
        onValueChange([]);
      }
      onInputValueChange("", "clearClick");
    };
    $[3] = onInputValueChange;
    $[4] = onValueChange;
    $[5] = type;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== onClick || $[8] !== t2) {
    t3 = composeEventHandlers(onClick, t2);
    $[7] = onClick;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props || $[11] !== t1 || $[12] !== t3) {
    t4 = <Primitive.button data-slot="combobox-clear" disabled={t1} onClick={t3} {...props} />;
    $[10] = props;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function ComboboxContent(t0) {
  const $ = _c(19);
  let children;
  let onInteractOutside;
  let onOpenAutoFocus;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      onOpenAutoFocus,
      onInteractOutside,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onInteractOutside;
    $[3] = onOpenAutoFocus;
    $[4] = props;
  } else {
    children = $[1];
    onInteractOutside = $[2];
    onOpenAutoFocus = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== onOpenAutoFocus) {
    t1 = composeEventHandlers(onOpenAutoFocus, _temp);
    $[5] = onOpenAutoFocus;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== onOpenAutoFocus) {
    t2 = composeEventHandlers(onOpenAutoFocus, _temp2);
    $[7] = onOpenAutoFocus;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== onInteractOutside) {
    t3 = composeEventHandlers(onInteractOutside, _temp3);
    $[9] = onInteractOutside;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children) {
    t4 = <CommandPrimitive.List data-slot="combobox-content">{children}</CommandPrimitive.List>;
    $[11] = children;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== props || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <PopoverPrimitive.Content asChild={true} onOpenAutoFocus={t1} onCloseAutoFocus={t2} onInteractOutside={t3} {...props}>{t4}</PopoverPrimitive.Content>;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
function _temp3(event_1) {
  if (event_1.target instanceof Element && event_1.target.hasAttribute("cmdk-input")) {
    event_1.preventDefault();
  }
}
function _temp2(event_0) {
  event_0.preventDefault();
}
function _temp(event) {
  event.preventDefault();
}
const ComboboxItemContext = createContext({
  isSelected: false
});
function useComboboxItem() {
  return useContext(ComboboxItemContext);
}
function findComboboxItemText(children: React.ReactNode) {
  let text = "";
  Children.forEach(children, child => {
    if (text) {
      return;
    }
    if (isValidElement<{
      children: React.ReactNode;
    }>(child)) {
      if (child.type === ComboboxItemText) {
        text = child.props.children as string;
      } else {
        text = findComboboxItemText(child.props.children);
      }
    }
  });
  return text;
}
export interface ComboboxItemProps extends Omit<React.ComponentProps<typeof CommandPrimitive.Item>, "value"> {
  value: string;
}
function ComboboxItem(t0) {
  const $ = _c(32);
  let children;
  let onMouseDown;
  let props;
  let valueProp;
  if ($[0] !== t0) {
    ({
      value: valueProp,
      children,
      onMouseDown,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onMouseDown;
    $[3] = props;
    $[4] = valueProp;
  } else {
    children = $[1];
    onMouseDown = $[2];
    props = $[3];
    valueProp = $[4];
  }
  const {
    type,
    value,
    onValueChange,
    onInputValueChange,
    onOpenChange
  } = useCombobox();
  let t1;
  if ($[5] !== children) {
    t1 = findComboboxItemText(children);
    $[5] = children;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const inputValue = t1;
  let t2;
  if ($[7] !== type || $[8] !== value || $[9] !== valueProp) {
    t2 = type === "single" ? value === valueProp : value.includes(valueProp);
    $[7] = type;
    $[8] = value;
    $[9] = valueProp;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const isSelected = t2;
  let t3;
  if ($[11] !== isSelected) {
    t3 = {
      isSelected
    };
    $[11] = isSelected;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== onMouseDown) {
    t4 = composeEventHandlers(onMouseDown, _temp4);
    $[13] = onMouseDown;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== inputValue || $[16] !== onInputValueChange || $[17] !== onOpenChange || $[18] !== onValueChange || $[19] !== type || $[20] !== value || $[21] !== valueProp) {
    t5 = () => {
      if (type === "multiple") {
        onValueChange(value.includes(valueProp) ? value.filter(v => v !== valueProp) : [...value, valueProp]);
        onInputValueChange("", "itemSelect");
      } else {
        onValueChange(valueProp);
        onInputValueChange(inputValue, "itemSelect");
        setTimeout(() => {
          onOpenChange(false);
        });
      }
    };
    $[15] = inputValue;
    $[16] = onInputValueChange;
    $[17] = onOpenChange;
    $[18] = onValueChange;
    $[19] = type;
    $[20] = value;
    $[21] = valueProp;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== children || $[24] !== inputValue || $[25] !== props || $[26] !== t4 || $[27] !== t5) {
    t6 = <CommandPrimitive.Item data-slot="combobox-item" onMouseDown={t4} onSelect={t5} value={inputValue} {...props}>{children}</CommandPrimitive.Item>;
    $[23] = children;
    $[24] = inputValue;
    $[25] = props;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t3 || $[30] !== t6) {
    t7 = <ComboboxItemContext.Provider value={t3}>{t6}</ComboboxItemContext.Provider>;
    $[29] = t3;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  return t7;
}
function _temp4(event) {
  event.preventDefault();
}
function ComboboxItemIndicator(props) {
  const $ = _c(2);
  const {
    isSelected
  } = useComboboxItem();
  if (!isSelected) {
    return null;
  }
  let t0;
  if ($[0] !== props) {
    t0 = <Primitive.span data-slot="combobox-item-indicator" aria-hidden={true} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export interface ComboboxItemTextProps extends React.ComponentProps<typeof Fragment> {
  children: string;
}
function ComboboxItemText(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Fragment {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function ComboboxTrigger(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <PopoverPrimitive.Trigger data-slot="combobox-trigger" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxAnchor(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <PopoverPrimitive.Anchor data-slot="combobox-anchor" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxPortal(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <PopoverPrimitive.Portal data-slot="combobox-portal" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxEmpty(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <CommandPrimitive.Empty data-slot="combobox-empty" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxLoading(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <CommandPrimitive.Loading data-slot="combobox-loading" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxGroup(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <CommandPrimitive.Group data-slot="combobox-group" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function ComboboxSeparator(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <CommandPrimitive.Separator data-slot="combobox-separator" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
export { ComboboxAnchor as Anchor, ComboboxClear as Clear, ComboboxContent as Content, ComboboxEmpty as Empty, ComboboxGroup as Group, ComboboxInput as Input, ComboboxItem as Item, ComboboxItemIndicator as ItemIndicator, ComboboxItemText as ItemText, ComboboxLoading as Loading, ComboboxPortal as Portal, Combobox as Root, ComboboxSeparator as Separator, ComboboxTagGroup as TagGroup, ComboboxTagGroupItem as TagGroupItem, ComboboxTagGroupItemRemove as TagGroupItemRemove, ComboboxTrigger as Trigger };